/**
 * FrontEnd/my-app/src/crm/portal/PortalDocumentViewer.js
 * Review, native typed signing, decline, and obligation clearing viewer modal.
 * Renders authentic multi-page Word DOCX legal agreement layout matching the original ERP document.
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import BASE_URL from '../../url';
import './PortalStyles.css';
import {
  LuX, LuFileText, LuCheck, LuDownload, LuShieldAlert,
  LuClock, LuUpload, LuCircleCheck, LuCircleAlert, LuUserCheck,
  LuPenTool, LuTrash2, LuType, LuImage, LuRotateCcw,
  LuPrinter, LuLayers
} from 'react-icons/lu';

const SIGNATURE_FONTS = [
  { id: 'Dancing Script', name: 'Dancing Script (Modern Script)', label: 'DANCING' },
  { id: 'Great Vibes', name: 'Great Vibes (Formal Calligraphy)', label: 'GREAT' },
  { id: 'Caveat', name: 'Caveat (Natural Hand)', label: 'CAVEAT' },
  { id: 'Sacramento', name: 'Sacramento (Delicate Cursive)', label: 'SACRAMENTO' }
];

const PortalDocumentViewer = ({ documentId: propDocId, onClose, onActionSuccess }) => {
  const { id: paramDocId } = useParams();
  const navigate = useNavigate();
  const documentId = propDocId || paramDocId;

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/portal/dashboard');
    }
  };

  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('VIEWER'); // 'VIEWER' | 'REQUIREMENTS'
  const [templatePreview, setTemplatePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(100);

  // Signing modal state
  const [showSignModal, setShowSignModal] = useState(false);
  const [signMode, setSignMode] = useState('TYPE'); // 'TYPE' | 'DRAW' | 'UPLOAD'
  const [typedName, setTypedName] = useState(() => {
    try {
      return localStorage.getItem('cp_preferred_sig_name') || '';
    } catch (e) {
      return '';
    }
  });
  const [selectedFont, setSelectedFont] = useState(() => {
    try {
      return localStorage.getItem('cp_preferred_sig_font') || 'Dancing Script';
    } catch (e) {
      return 'Dancing Script';
    }
  });
  const [hasDrawn, setHasDrawn] = useState(false);
  const [drawnSigData, setDrawnSigData] = useState(null);
  const [uploadedSigData, setUploadedSigData] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [consent, setConsent] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState('');

  const handleSelectFont = (fontId) => {
    setSelectedFont(fontId);
    try {
      localStorage.setItem('cp_preferred_sig_font', fontId);
    } catch (e) {}
  };

  const handleTypedNameChange = (val) => {
    setTypedName(val);
    try {
      localStorage.setItem('cp_preferred_sig_name', val);
    } catch (e) {}
  };

  const openSignModal = () => {
    try {
      const savedFont = localStorage.getItem('cp_preferred_sig_font');
      if (savedFont) setSelectedFont(savedFont);
      const savedName = localStorage.getItem('cp_preferred_sig_name');
      if (savedName) {
        setTypedName(savedName);
      } else if (doc?.myAction?.signerName) {
        setTypedName(doc.myAction.signerName);
      }
    } catch (e) {}
    setShowSignModal(true);
  };

  // Canvas and File refs
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const fileInputRef = useRef(null);

  // Decline modal state
  const [showDeclineModal, setShowDeclineModal] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [declining, setDeclining] = useState(false);

  // Requirements upload state
  const [uploadingReqId, setUploadingReqId] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const token = localStorage.getItem('portal_token') || '';
  const getAuthHeaders = () => {
    return {
      headers: { Authorization: `Bearer ${localStorage.getItem('portal_token') || token}` },
      withCredentials: true
    };
  };

  const fetchDocumentDetail = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/portal/documents/${documentId}`, getAuthHeaders());
      const docData = res.data;
      setDoc(docData);

      // Fetch authentic template preview from server
      const tmplId = docData.templateId || (docData.documentTypeCode === 'PO' ? 2 : 1);
      setPreviewLoading(true);
      axios.get(`${BASE_URL}/api/crm/commercial-documents/templates/${tmplId}/preview`)
        .then(tRes => {
          if (tRes.data?.success) {
            setTemplatePreview(tRes.data);
          }
        })
        .catch(err => {
          console.warn('Could not fetch template preview for portal:', err);
        })
        .finally(() => {
          setPreviewLoading(false);
        });

      if (docData?.myAction?.canSign) {
        // Pre-fill user's name
        const userObj = JSON.parse(localStorage.getItem('portal_user') || '{}');
        const defaultName = userObj?.contact ? `${userObj.contact.firstName} ${userObj.contact.lastName}` : '';
        setTypedName(defaultName);
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to load document details.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const savedToken = localStorage.getItem('portal_token');
    if (!savedToken) {
      navigate(`/portal/login?dest=/portal/documents/${documentId || ''}`);
      return;
    }
    if (documentId) {
      fetchDocumentDetail();
    }
  }, [documentId, navigate]);

  // Restore drawn signature if returning to DRAW tab
  useEffect(() => {
    if (showSignModal && signMode === 'DRAW' && canvasRef.current && drawnSigData) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);
      };
      img.src = drawnSigData;
    }
  }, [showSignModal, signMode, drawnSigData]);

  // ─── Drawing Canvas Handlers ─────────────────────────────────────────
  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches && e.touches.length > 0) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.strokeStyle = '#0f172a';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    isDrawingRef.current = true;
    setHasDrawn(true);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const { x, y } = getCoordinates(e, canvas);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    const canvas = canvasRef.current;
    if (canvas) {
      setDrawnSigData(canvas.toDataURL('image/png'));
    }
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
    setDrawnSigData(null);
  };

  // ─── File Upload Handler for Signature Image ─────────────────────────
  const handleSignatureFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please upload a valid image file (PNG, JPG, SVG).');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      alert('Signature image file size must be less than 2MB.');
      return;
    }

    setUploadedFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      setUploadedSigData(event.target.result);
    };
    reader.readAsDataURL(file);
  };

  const clearUploadedSignature = () => {
    setUploadedSigData(null);
    setUploadedFileName('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // ─── Unified Multi-Format Sign Handler ───────────────────────────────────────
  const handleSign = async (e) => {
    e.preventDefault();
    if (!consent) {
      setSignError('Please check the legal consent box to proceed.');
      return;
    }

    if (!typedName.trim()) {
      setSignError('Please type your legal signatory name.');
      return;
    }

    if (signMode === 'DRAW' && (!hasDrawn || !drawnSigData)) {
      setSignError('Please draw your signature on the pad above before confirming.');
      return;
    }

    if (signMode === 'UPLOAD' && !uploadedSigData) {
      setSignError('Please upload a signature image before confirming.');
      return;
    }

    setSigning(true);
    setSignError('');

    const signingMethod = signMode === 'DRAW' ? 'DIRECT_DRAW' : signMode === 'UPLOAD' ? 'IMAGE_UPLOAD' : 'PORTAL_TYPED';
    const signatureImage = signMode === 'DRAW' ? drawnSigData : signMode === 'UPLOAD' ? uploadedSigData : null;

    try {
      const res = await axios.post(`${BASE_URL}/api/portal/documents/${documentId}/sign`, {
        signerId: doc.myAction.signerId,
        typedName: typedName.trim(),
        signingMethod: signingMethod,
        signatureImage: signatureImage,
        selectedFont: signMode === 'TYPE' ? selectedFont : null,
        consent: true
      }, getAuthHeaders());

      if (res.data.status === 'ok') {
        setShowSignModal(false);
        fetchDocumentDetail();
        if (onActionSuccess) onActionSuccess();
      }
    } catch (err) {
      setSignError(err.response?.data?.error?.message || 'Error occurred while signing document.');
    } finally {
      setSigning(false);
    }
  };

  // Handle Decline
  const handleDecline = async (e) => {
    e.preventDefault();
    if (!declineReason.trim()) {
      alert('Please provide a reason for declining.');
      return;
    }

    setDeclining(true);
    try {
      const res = await axios.post(`${BASE_URL}/api/portal/documents/${documentId}/decline`, {
        signerId: doc.myAction.signerId,
        reason: declineReason.trim()
      }, getAuthHeaders());

      if (res.data.status === 'ok') {
        setShowDeclineModal(false);
        fetchDocumentDetail();
        if (onActionSuccess) onActionSuccess();
      }
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to submit decline response.');
    } finally {
      setDeclining(false);
    }
  };

  // Handle Obligation Upload
  const handleUploadRequirement = async (reqId) => {
    if (!uploadFile) {
      alert('Please select a file to upload.');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', uploadFile);

    try {
      const headers = getAuthHeaders();
      const res = await axios.post(
        `${BASE_URL}/api/portal/documents/${documentId}/requirements/${reqId}/upload`,
        formData,
        {
          ...headers,
          headers: {
            ...headers.headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (res.data.status === 'ok') {
        setUploadingReqId(null);
        setUploadFile(null);
        fetchDocumentDetail();
      }
    } catch (err) {
      alert(err.response?.data?.error?.message || 'Failed to upload requirement file.');
    } finally {
      setUploading(false);
    }
  };

  // ─── Authentic Legal Document Formatters ──────────────────────────────
  const formatUtcDateTime = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      timeZone: 'UTC',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }) + ' UTC';
  };

  const formatUtcDate = (val) => {
    if (!val) return '';
    const d = new Date(val);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { timeZone: 'UTC' });
  };

  const getActiveTerms = (document) => {
    if (!document) return {};
    let parsed = {};
    try {
      const notesRaw = document.notes || document.Notes;
      if (notesRaw && typeof notesRaw === 'string' && notesRaw.trim().startsWith('{')) {
        parsed = JSON.parse(notesRaw);
      }
    } catch (e) {}

    const cpComp = document.counterpartyCompany || {};
    const defaultProviderAddr = [
      cpComp.addressLine1 || document.CounterpartyAddressLine1,
      cpComp.city || document.CounterpartyCity,
      cpComp.state || document.CounterpartyState,
      cpComp.postalCode || document.CounterpartyPostalCode
    ].filter(Boolean).join(', ');

    const signersList = document.signers || [];
    const govSigner = signersList.find(s => (s.partySide || s.PartySide) === 'GOVERNING');
    const cpSigner = signersList.find(s => (s.partySide || s.PartySide) === 'COUNTERPARTY');

    const effDate = document.effectiveFromDate || document.EffectiveFromDate;

    return {
      providerLegalName: parsed.providerLegalName || cpComp.legalName || cpComp.name || document.CounterpartyLegalName || document.CounterpartyName || 'Tata Consultancy Service',
      providerAddress: parsed.providerAddress || defaultProviderAddr || 'Principal business address on record',
      providerTaxId: parsed.providerTaxId || cpComp.taxId || document.CounterpartyTaxId || 'On File with Prophecy',
      termYears: parsed.termYears || '3',
      subcontractingCapPct: parsed.subcontractingCapPct || '40',
      paymentTerms: parsed.paymentTerms || 'Net 60',
      earlyPaymentDiscount45: parsed.earlyPaymentDiscount45 || '3%',
      earlyPaymentDiscount30: parsed.earlyPaymentDiscount30 || '5%',
      booksRetentionYears: parsed.booksRetentionYears || '7',
      nonSolicitationFee: parsed.nonSolicitationFee || '3,500',
      customClauses: parsed.customClauses || '',
      effectiveDateStr: effDate
        ? new Date(effDate).toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' })
        : 'October 5, 2026',
      govSigner: govSigner ? {
        ...govSigner,
        StatusCode: govSigner.statusCode || govSigner.StatusCode,
        SignedAtUtc: govSigner.signedAtUtc || govSigner.SignedAtUtc,
        TypedName: govSigner.typedName || govSigner.name || govSigner.SignerName,
        SignerName: govSigner.name || govSigner.SignerName,
        SignerTitle: govSigner.title || govSigner.SignerTitle || 'Chief Executive Officer',
        SignatureImage: govSigner.signatureImage || govSigner.SignatureImage || null,
        SelectedFont: govSigner.selectedFont || govSigner.SelectedFont || null,
        SigningMethod: govSigner.signingMethod || govSigner.SigningMethod || null
      } : null,
      cpSigner: cpSigner ? {
        ...cpSigner,
        StatusCode: cpSigner.statusCode || cpSigner.StatusCode,
        SignedAtUtc: cpSigner.signedAtUtc || cpSigner.SignedAtUtc,
        TypedName: cpSigner.typedName || cpSigner.name || cpSigner.SignerName,
        SignerName: cpSigner.name || cpSigner.SignerName,
        SignerTitle: cpSigner.title || cpSigner.SignerTitle || 'Authorized Representative',
        SignatureImage: cpSigner.signatureImage || cpSigner.SignatureImage || null,
        SelectedFont: cpSigner.selectedFont || cpSigner.SelectedFont || null,
        SigningMethod: cpSigner.signingMethod || cpSigner.SigningMethod || null
      } : null
    };
  };

  const formatDocumentHtml = (html) => {
    if (!html) return '';
    let res = html;

    res = res.replace(/<li>([A-Za-z0-9\s&,/-]+?\.)(\s+)/g, '<li><strong style="text-decoration: underline; font-weight: 700;">$1</strong>$2');
    res = res.replace(
      /<p><strong>(MASTER SERVICES AGREEMENT|VENDOR WORK ORDER|PURCHASE ORDER.*?)<\/strong><\/p>/gi,
      '<div class="doc-title-heading">$1</div>'
    );
    res = res.replace(
      /<p><strong>WITNESSETH<\/strong><\/p>/gi,
      '<p class="doc-section-heading"><strong>WITNESSETH</strong></p>'
    );

    return res;
  };

  const getDocPages = (htmlStr, previewObj) => {
    if (previewObj?.pages && Array.isArray(previewObj.pages) && previewObj.pages.length > 1) {
      return previewObj.pages;
    }
    if (!htmlStr) return [];

    const splitPhrases = [
      'Requisite Skills.',
      'Legal Compliance.',
      'Provider represents and warrants that it will use best technical practices',
      'Non-Solicitation.',
      'notice to the other party as provided herein.',
      'the parties as if they were originals.'
    ];

    let remaining = htmlStr;
    const rawPages = [];

    for (const phrase of splitPhrases) {
      const idx = remaining.indexOf(phrase);
      if (idx !== -1) {
        let cutIdx = idx;
        const liBefore = remaining.lastIndexOf('<li', idx);
        const pBefore = remaining.lastIndexOf('<p', idx);
        const bestBefore = Math.max(liBefore, pBefore);
        if (bestBefore !== -1 && idx - bestBefore < 120) {
          cutIdx = bestBefore;
        }
        rawPages.push(remaining.substring(0, cutIdx));
        remaining = remaining.substring(cutIdx);
      }
    }
    if (remaining) {
      rawPages.push(remaining);
    }

    return rawPages.map((pageHtml, idx) => {
      let fixed = pageHtml;
      const firstLi = fixed.indexOf('<li');
      const firstOl = fixed.indexOf('<ol');
      if (firstLi !== -1 && (firstOl === -1 || firstLi < firstOl)) {
        let startNum = 1;
        if (idx === 1) startNum = 4;
        else if (idx === 2) startNum = 9;
        else if (idx === 4) startNum = 20;
        else if (idx === 5) startNum = 25;
        fixed = `<ol start="${startNum}">` + fixed;
      }

      const olOpenCount = (fixed.match(/<ol[^>]*>/g) || []).length;
      const olCloseCount = (fixed.match(/<\/ol>/g) || []).length;
      if (olOpenCount > olCloseCount) {
        fixed += '</ol>'.repeat(olOpenCount - olCloseCount);
      }
      if (olCloseCount > olOpenCount) {
        fixed = '<ol>'.repeat(olCloseCount - olOpenCount) + fixed;
      }

      return fixed;
    });
  };

  const interpolateDocData = (pageHtml, terms, document, pageIdx, totalPages) => {
    if (!pageHtml) return '';
    let res = pageHtml;

    const isPO = document?.documentTypeCode === 'PO' || document?.DocumentTypeCode === 'PO' || pageHtml.includes('VENDOR WORK ORDER') || pageHtml.includes('PURCHASE ORDER');

    // === PO DOCUMENT INTERPOLATION ===
    if (isPO) {
      const po = document?.poDetail || {};
      const billRateVal = (po.BillRate != null && po.BillRate !== '')
        ? `$${Number(po.BillRate).toFixed(2)} / ${po.BillRateType || 'HOURLY'}`
        : '$0.00 / HOURLY';

      if (terms.providerLegalName) {
        res = res.replace(
          /(signed between (?:<strong>)?Prophecy Consulting Inc(?:<\/strong>)? and\s*(?:<strong>)?)(_{4,}|\[.*?\])(?:<\/strong>)?/gi,
          `$1${terms.providerLegalName}</strong>`
        );
        res = res.replace(/and\s*<strong>_{5,}<\/strong>/gi, `and <strong>${terms.providerLegalName}</strong>`);
        res = res.replace(/and\s*_{5,}/gi, `and <strong>${terms.providerLegalName}</strong>`);
      }

      res = res.replace(
        /(Consultant[’']s Full Name<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi,
        `$1<p><strong>${po.ConsultantName || 'Sai Shashank Rachumalla'}</strong></p>$3`
      );
      res = res.replace(
        /(Client Name<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi,
        `$1<p><strong>${po.EndClientName || 'Direct Engagement'}</strong></p>$3`
      );

      let workLocIndex = 0;
      res = res.replace(/(Work Location<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi, (match, p1, p2, p3) => {
        workLocIndex++;
        if (workLocIndex === 1) {
          return `${p1}<p><strong>${po.WorkLocation || 'Remote / Client Site'}</strong></p>${p3}`;
        } else {
          const secondVal = po.StartDate ? `Start Date: ${new Date(po.StartDate).toLocaleDateString()}` : (po.WorkLocation || 'Remote / Client Site');
          return `${p1}<p><strong>${secondVal}</strong></p>${p3}`;
        }
      });

      res = res.replace(
        /(Duration<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi,
        `$1<p><strong>${po.DurationText || '06+ months'}</strong></p>$3`
      );

      res = res.replace(
        /(Bill Rate<\/p><\/td>\s*<td>)([\s\S]*?)(<\/td>)/gi,
        `$1<p><strong>${billRateVal}</strong></p>$3`
      );

      res = res.replace(
        /Payment Term:\s*_{4,}\s*days/gi,
        `Payment Term: <strong>${po.PaymentTermDays || 60} days (Net ${po.PaymentTermDays || 60})</strong>`
      );

      const govSigned = terms.govSigner?.StatusCode === 'SIGNED';
      const cpSigned = terms.cpSigner?.StatusCode === 'SIGNED';
      const govDate = terms.govSigner?.SignedAtUtc ? formatUtcDate(terms.govSigner.SignedAtUtc) : '';
      const cpDate = terms.cpSigner?.SignedAtUtc ? formatUtcDate(terms.cpSigner.SignedAtUtc) : '';
      const govTime = terms.govSigner?.SignedAtUtc ? formatUtcDateTime(terms.govSigner.SignedAtUtc) : '';
      const cpTime = terms.cpSigner?.SignedAtUtc ? formatUtcDateTime(terms.cpSigner.SignedAtUtc) : '';
      const cpName = terms.cpSigner?.TypedName || terms.cpSigner?.SignerName || terms.providerLegalName;
      const cpTitle = terms.cpSigner?.SignerTitle || 'Authorized Representative';

      const poSignaturesHtml = `
<p style="margin-top: 24px; font-weight: 600;">The undersigned has/have read, understand(s) and agree(s) to terms and conditions herein.</p>
<table style="width: 100%; border-collapse: collapse; margin-top: 12px;">
  <tbody>
    <tr>
      <td style="width: 50%; font-weight: 700; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 8px 12px;">
        Accepted by: <strong>Prophecy Consulting Inc</strong>
      </td>
      <td style="width: 50%; font-weight: 700; background-color: #f8fafc; border: 1px solid #94a3b8; padding: 8px 12px;">
        Accepted by: <strong>${terms.providerLegalName}</strong>
      </td>
    </tr>
    <tr>
      <td style="border: 1px solid #94a3b8; padding: 12px;">
        ${govSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.govSigner?.SignatureImage ? `
              <img src="${terms.govSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.govSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #166534; font-weight: 600;">${terms.govSigner.TypedName || 'Senthil Marimuthu'}</span>
            `}
            <span style="font-size: 0.72rem; color: #166534; font-weight: 600;">✓ Digitally Signed on ${govTime}</span>
          </div>
        ` : `<div style="border-bottom: 1px solid #94a3b8; width: 180px; margin: 16px 0 6px;"><span style="font-size: 0.75rem; color: #94a3b8;">By: ______________________</span></div>`}
        <div style="font-size: 0.85rem; margin-top: 8px;">
          <div><strong>Name:</strong> Senthil Marimuthu</div>
          <div><strong>Title:</strong> Chief Executive Officer</div>
          <div><strong>Date:</strong> ${govSigned ? `<strong>${govDate}</strong>` : '_______________'}</div>
        </div>
      </td>
      <td style="border: 1px solid #94a3b8; padding: 12px;">
        ${cpSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.cpSigner?.SignatureImage ? `
              <img src="${terms.cpSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.cpSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #166534; font-weight: 600;">${cpName}</span>
            `}
            <span style="font-size: 0.72rem; color: #166534; font-weight: 600;">✓ Digitally Signed on ${cpTime}</span>
          </div>
        ` : `<div style="border-bottom: 1px solid #94a3b8; width: 180px; margin: 16px 0 6px;"><span style="font-size: 0.75rem; color: #94a3b8;">By: ______________________</span></div>`}
        <div style="font-size: 0.85rem; margin-top: 8px;">
          <div><strong>Name:</strong> <strong>${cpName}</strong></div>
          <div><strong>Title:</strong> <strong>${cpTitle}</strong></div>
          <div><strong>Date:</strong> ${cpSigned ? `<strong>${cpDate}</strong>` : '_______________'}</div>
        </div>
      </td>
    </tr>
  </tbody>
</table>`;

      res = res.replace(/<p>The undersigned has\/have read, understand\(s\) and agree\(s\)[\s\S]*?Date:\s*<\/p>/gi, poSignaturesHtml);
      return res;
    }

    // === MSA DOCUMENT INTERPOLATION ===
    // Page 1: Effective date, Subcontractor name, Address, Tax ID
    if (pageIdx === 0) {
      if (terms.effectiveDateStr) {
        res = res.replace(/7<sup>th<\/sup>\s*August\s*2026/gi, terms.effectiveDateStr);
        res = res.replace(/August\s+7,?\s+2026/gi, terms.effectiveDateStr);
        res = res.replace(/\[\s*Date\s*\]/gi, terms.effectiveDateStr);
      }
      if (terms.providerLegalName) {
        res = res.replace(/_____________(\s*<\/strong>\s*located\s*at)/gi, `<strong>${terms.providerLegalName}</strong>$1`);
        res = res.replace(/\[\s*Subcontractor\s+Name\s*\]/gi, terms.providerLegalName);
        res = res.replace(/\[\s*Provider\s+Name\s*\]/gi, terms.providerLegalName);
      }
      if (terms.providerAddress) {
        res = res.replace(/_{5,}(\s*\(“Provider”\))/gi, `${terms.providerAddress}$1`);
        res = res.replace(/\[\s*Provider\s+Address\s*\]/gi, terms.providerAddress);
      }
      if (terms.providerTaxId) {
        res = res.replace(/(Federal Tax ID<strong>:\s*)(_{5,})(\.<\/strong>)/gi, `$1<strong>${terms.providerTaxId}</strong>$3`);
        res = res.replace(/\[\s*Federal\s+Tax\s+ID\s*\]/gi, terms.providerTaxId);
      }
    }

    // Page 6: Notice addresses
    if (pageIdx === 5 || (totalPages <= 6 && pageIdx === totalPages - 2)) {
      if (terms.providerLegalName) {
        res = res.replace(
          /If to Provider:\s*<\/p><p><strong>_{5,}<\/strong><\/p><p><strong>_{5,}<\/strong><\/p><p><strong>_{5,}<\/strong>/gi,
          `If to Provider:</p><p><strong>${terms.providerLegalName}</strong></p><p><strong>${terms.providerAddress || '___________________________'}</strong></p>`
        );
      }
    }

    // Last Page (Signatures table)
    if (pageIdx === totalPages - 1 || pageHtml.includes('IN WITNESS WHEREOF')) {
      const govSigned = terms.govSigner?.StatusCode === 'SIGNED';
      const cpSigned = terms.cpSigner?.StatusCode === 'SIGNED';
      const govDate = terms.govSigner?.SignedAtUtc ? formatUtcDate(terms.govSigner.SignedAtUtc) : '';
      const cpDate = terms.cpSigner?.SignedAtUtc ? formatUtcDate(terms.cpSigner.SignedAtUtc) : '';
      const govTime = terms.govSigner?.SignedAtUtc ? formatUtcDateTime(terms.govSigner.SignedAtUtc) : '';
      const cpTime = terms.cpSigner?.SignedAtUtc ? formatUtcDateTime(terms.cpSigner.SignedAtUtc) : '';
      const govName = terms.govSigner?.TypedName || terms.govSigner?.SignerName || 'Senthil Marimuthu';
      const cpName = terms.cpSigner?.TypedName || terms.cpSigner?.SignerName || terms.providerLegalName;
      const govTitle = terms.govSigner?.SignerTitle || 'Chief Executive Officer';
      const cpTitle = terms.cpSigner?.SignerTitle || 'Authorized Representative';
      const providerCompName = terms.providerLegalName || 'Tata Consultancy Service';

      const signaturesTableHtml = `
<table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
  <tbody>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p><strong>Prophecy Consulting Inc</strong></p></td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p><strong>${providerCompName}</strong></p></td>
    </tr>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; vertical-align: top; background-color: #ffffff;">
        ${govSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.govSigner?.SignatureImage ? `
              <img src="${terms.govSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.govSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #166534; font-weight: 600;">${govName}</span>
            `}
            <span style="font-size: 0.72rem; color: #166534; font-weight: 600;">✓ Digitally Signed on ${govTime}</span>
          </div>
        ` : '<p>By ______________________</p>'}
      </td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; vertical-align: top; background-color: #ffffff;">
        ${cpSigned ? `
          <div style="min-height: 48px; display: flex; flex-direction: column; justify-content: center;">
            ${terms.cpSigner?.SignatureImage ? `
              <img src="${terms.cpSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
            ` : `
              <span style="font-family: ${terms.cpSigner?.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #166534; font-weight: 600;">${cpName}</span>
            `}
            <span style="font-size: 0.72rem; color: #166534; font-weight: 600;">✓ Digitally Signed on ${cpTime}</span>
          </div>
        ` : '<p>By ______________________</p>'}
      </td>
    </tr>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Name: <strong>${govName}</strong></p></td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Name: <strong>${cpName}</strong></p></td>
    </tr>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Title: <strong>${govTitle}</strong></p></td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Title: <strong>${cpTitle}</strong></p></td>
    </tr>
    <tr>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Date: ${govSigned ? `<strong>${govDate}</strong>` : '_______________'}</p></td>
      <td style="width: 50% !important; border: 1px solid #94a3b8; padding: 10px 14px; font-weight: normal; background-color: #ffffff;"><p>Date: ${cpSigned ? `<strong>${cpDate}</strong>` : '___________________'}</p></td>
    </tr>
  </tbody>
</table>`;

      if (res.includes('<table')) {
        res = res.replace(/<table[\s\S]*?<\/table>/gi, signaturesTableHtml.trim());
      } else {
        res += signaturesTableHtml;
      }
    }

    return res;
  };

  const getFallbackDocPages = (terms, document) => {
    return [
      // Page 1
      `<div class="doc-title-heading">MASTER SERVICES AGREEMENT</div>
<p>THIS MASTER SERVICES AGREEMENT (“Agreement”) is made as of <strong>${terms.effectiveDateStr}</strong> by and between <strong>Prophecy Consulting Inc,</strong> a CA Corporation located at <strong>7545 Irvine Center Dr, Ste 200, Irvine, CA 92618</strong> (“<strong>Client</strong>”) with Federal Tax ID: <strong>46-5026539</strong>, and <strong>${terms.providerLegalName}</strong> located at <strong>${terms.providerAddress}</strong> (“<strong>Provider</strong>”) with Federal Tax ID: <strong>${terms.providerTaxId}</strong>.</p>
<p class="doc-section-heading"><strong>WITNESSETH</strong></p>
<p>WHEREAS Provider is an independent contractor who provides consulting services; and</p>
<p>WHEREAS Client wishes to purchase consulting services in form of Contract Employees to be placed on a site of Client; and</p>
<p>WHEREAS, as used herein, the term “Contract Employee(s)” means an individual employed by Provider, temporarily placed with Client pursuant to this Agreement.</p>
<p>NOW, THEREFORE, in consideration of the mutual premises hereinafter set forth and of other good and valuable consideration, the receipt and sufficiency of which is hereby acknowledged, Client and Provider, intending to be legally bound, hereby agree as follows:</p>
<ol>
  <li><strong style="text-decoration: underline; font-weight: 700;">Engagement.</strong> Client hereby engages Provider, on a non-exclusive basis, to provide Services pursuant to one or more purchase orders to be executed by the parties (each a “Purchase Order”), upon the terms and conditions set forth herein, and Provider hereby accepts the engagement. Each Purchase Order shall be mutually agreed upon in writing by Provider and Client before commencement of the work called for by such Purchase Order.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Termination.</strong> This Agreement shall commence on the Effective Date and continue for a term of ${terms.termYears} (${terms.termYears === '3' ? 'three (3)' : terms.termYears}) years with automatic one (1) year renewals unless terminated as set forth herein. Either party may terminate this Agreement with thirty (30) days written notice. Client may terminate immediately for performance issues, insolvency, or breach of obligations.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Contract Employees.</strong> Provider shall supply Contract Employees per Client requisitions, ensuring they have requisite skills, work authorization, and complete all background and compliance checks as mandated by Client. Provider shall not subcontract labor without Client’s prior written approval and total subcontracted personnel shall not exceed ${terms.subcontractingCapPct}% of all Provider personnel assigned.</li>
</ol>`,

      // Page 2
      `<ol start="4">
  <li><strong style="text-decoration: underline; font-weight: 700;">Requisite Skills.</strong> Provider specifically acknowledges and agrees that all Contract Employees shall have the requisite knowledge, expertise, experience and training to perform the Services in a timely, competent, and professional manner.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Equal Opportunity Employment.</strong> Provider will provide qualified Contract Employees without regard to race, religion, age, gender, national origin, sexual orientation, physical handicap or marital status.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Independent Contractor.</strong> Provider acts as an independent contractor. Neither Provider nor Contract Employee will represent itself as an employee, agent or legal representative of Client.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Taxes.</strong> Provider will be responsible for paying all federal, state, municipal, or other taxes as required by any law, including employment taxes related to performance of the Services.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Invoicing & Payments.</strong> Provider will submit invoices based on approved time and expenses. Provider will be paid within sixty (60) days from the date of receipt of a correct and undisputed invoice (“<strong>${terms.paymentTerms} Payment Terms</strong>”).
    <br/><br/>
    <em>Early payment discount option:</em>
    <ul>
      <li><strong>${terms.earlyPaymentDiscount45}</strong> of total invoice amount for Net 45 Payment Terms.</li>
      <li><strong>${terms.earlyPaymentDiscount30}</strong> of total invoice amount for Net 30 Payment Terms.</li>
    </ul>
  </li>
</ol>`,

      // Page 3
      `<ol start="9">
  <li><strong style="text-decoration: underline; font-weight: 700;">Expenses.</strong> Provider shall be responsible for all expenses incurred in the performance of the Services unless specifically agreed upon by the parties in writing in advance.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Legal Compliance.</strong> Provider shall comply with all applicable laws and maintain all necessary permits, licenses, and immigration compliance.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Books and Records.</strong> Provider shall preserve for <strong>Seven (${terms.booksRetentionYears}) years</strong> accurate and detailed records of all timesheets, ledgers, books of account, and invoices relating to the Services.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Confidentiality.</strong> All information shared is confidential and may not be disclosed without Client’s written consent.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Data Protection.</strong> Provider shall comply with applicable data privacy laws, implement SOC 2 Type II / ISO 27001 level security controls, and notify Client within forty-eight (48) hours of any security incident.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Asset Return.</strong> Provider must ensure Contract Employees return all Client assets within two (2) weeks after termination of assignment.</li>
</ol>`,

      // Page 4
      `<ol start="15">
  <li><strong style="text-decoration: underline; font-weight: 700;">Non-Disclosure of Confidential Information.</strong> Provider will not, during or after termination without prior written authorization, disclose or make use of Confidential Information in any medium.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">E-mail.</strong> Contract Employees acquire no privacy rights on Client's electronic systems. Client reserves the right to monitor communications.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Provider Warranties.</strong> Provider warrants that it will use best technical practices, all work products will be original or fully licensed, and will comply with Client policies.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Intellectual Property.</strong> All work product performed by any Contract Employee is deemed work-for-hire of Client and exclusively owned by Client.</li>
</ol>`,

      // Page 5
      `<ol start="19">
  <li><strong style="text-decoration: underline; font-weight: 700;">Indemnification.</strong> Provider shall indemnify, defend, and hold harmless Client and its clients from any claims arising from Provider’s services, worker misclassification, or wage/tax obligations.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Breach; Attorney’s Fees.</strong> The prevailing party in any action shall be entitled from the other party its reasonable attorney fees and expenses of litigation.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Modifications.</strong> No change of this Agreement shall be valid unless in writing and signed by both parties.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Non-Solicitation.</strong> Provider agrees not to solicit Client employees during the Agreement and for twelve (12) months thereafter. A hiring fee of USD <strong>${terms.nonSolicitationFee}</strong> applies if hired within three (3) months.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Contact with Client.</strong> Provider shall not make direct contact with Client’s clients without Client’s prior written approval.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Injunction.</strong> Client may pursue injunctive relief in addition to legal remedies for breach of non-solicitation or confidentiality.</li>
</ol>`,

      // Page 6
      `<ol start="25">
  <li><strong style="text-decoration: underline; font-weight: 700;">Non-Solicitation of Personnel.</strong> During the term and for one (1) year thereafter, neither party shall directly solicit the other party's personnel without written consent.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Integration.</strong> This Agreement constitutes the entire agreement between the parties with respect to the subject matter hereof.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Notices.</strong> Formal notice addresses:
    <p>If to Client:</p>
    <p><strong>Prophecy Consulting Inc</strong></p>
    <p><strong>7545 Irvine Center Drive, Suite 200, Irvine, CA 92618</strong></p>
    <p>If to Provider:</p>
    <p><strong>${terms.providerLegalName}</strong></p>
    <p><strong>${terms.providerAddress}</strong></p>
  </li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Governing Law.</strong> This Agreement shall be governed by and interpreted in accordance with the laws of the State of California.</li>
  <li><strong style="text-decoration: underline; font-weight: 700;">Counterparts.</strong> This Agreement may be executed in counterparts, each of which shall be deemed an original and binding upon the parties.</li>
</ol>`,

      // Page 7
      `<p>IN WITNESS WHEREOF, the parties hereto have duly executed and delivered this Agreement as of the day and year first above written.</p>
<table>
  <tbody>
    <tr>
      <td><p><strong>Prophecy Consulting Inc</strong></p></td>
      <td><p><font color="#0f172a"><strong>${terms.providerLegalName}</strong></font></p></td>
    </tr>
    <tr>
      <td>${terms.govSigner?.StatusCode === 'SIGNED' ? `
        <p style="margin: 0;">
          ${terms.govSigner.SignatureImage ? `
            <img src="${terms.govSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
          ` : `
            <span style="font-family: ${terms.govSigner.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #166534; font-weight: 600;">${terms.govSigner.TypedName || 'Senthil Marimuthu'}</span>
          `}
          <span style="font-size: 0.72rem; color: #166534; font-weight: 600;">✓ Digitally Signed on ${formatUtcDateTime(terms.govSigner.SignedAtUtc)}</span>
        </p>
      ` : '<p>By ______________________</p>'}</td>
      <td>${terms.cpSigner?.StatusCode === 'SIGNED' ? `
        <p style="margin: 0;">
          ${terms.cpSigner.SignatureImage ? `
            <img src="${terms.cpSigner.SignatureImage}" alt="Signature" style="max-height: 48px; max-width: 190px; object-fit: contain; display: block; margin-bottom: 4px;" />
          ` : `
            <span style="font-family: ${terms.cpSigner.SelectedFont || 'cursive'}; font-size: 1.25rem; color: #166534; font-weight: 600;">${terms.cpSigner.TypedName || terms.cpSigner.SignerName || terms.providerLegalName}</span>
          `}
          <span style="font-size: 0.72rem; color: #166534; font-weight: 600;">✓ Digitally Signed on ${formatUtcDateTime(terms.cpSigner.SignedAtUtc)}</span>
        </p>
      ` : '<p>By ______________________</p>'}</td>
    </tr>
    <tr>
      <td><p>Name: <strong>Senthil Marimuthu</strong></p></td>
      <td><p>Name: <strong>${terms.cpSigner?.TypedName || terms.cpSigner?.SignerName || terms.providerLegalName}</strong></p></td>
    </tr>
    <tr>
      <td><p>Title: <strong>Chief Executive Officer</strong></p></td>
      <td><p>Title: <strong>${terms.cpSigner?.SignerTitle || 'Authorized Representative'}</strong></p></td>
    </tr>
    <tr>
      <td><p>Date: ${terms.govSigner?.SignedAtUtc ? `<strong>${formatUtcDate(terms.govSigner.SignedAtUtc)}</strong>` : '_______________'}</p></td>
      <td><p>Date: ${terms.cpSigner?.SignedAtUtc ? `<strong>${formatUtcDate(terms.cpSigner.SignedAtUtc)}</strong>` : '_______________'}</p></td>
    </tr>
  </tbody>
</table>`
    ];
  };

  if (!documentId) {
    return (
      <div className="cp-modal-overlay">
        <div className="cp-modal-panel" style={{ maxWidth: '480px', height: 'auto', padding: '36px', textAlign: 'center' }}>
          <h3 style={{ fontSize: '18px', fontWeight: 750, color: 'var(--cp-ink)', marginBottom: '8px' }}>
            No Document Specified
          </h3>
          <p style={{ color: 'var(--cp-muted)', fontSize: '14px', marginBottom: '20px' }}>
            Please select an agreement from your documents repository.
          </p>
          <button
            type="button"
            className="cp-btn-primary"
            style={{ margin: '0 auto' }}
            onClick={handleClose}
          >
            Go to Repository
          </button>
        </div>
      </div>
    );
  }

  const terms = getActiveTerms(doc);
  const rawPages = (templatePreview?.pages && templatePreview.pages.length > 0)
    ? (templatePreview.pages.length === 1 ? getDocPages(templatePreview.pages[0], templatePreview) : templatePreview.pages)
    : (templatePreview?.html ? getDocPages(templatePreview.html, templatePreview) : getFallbackDocPages(terms, doc));

  return (
    <div className="cp-modal-overlay">
      <div className="cp-modal-panel" style={{ maxWidth: '1360px', width: '96vw', height: '94vh' }}>
        {/* Top Header */}
        <div className="cp-modal-header" style={{ padding: '12px 24px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="cp-logo-icon" style={{ width: '34px', height: '34px', fontSize: '15px' }}>
              <LuFileText size={18} />
            </div>
            <div>
              <div style={{ fontSize: '15.5px', fontWeight: 700, color: 'var(--cp-ink)', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span>{doc?.documentNumber} — {doc?.title || 'Master Services Agreement'}</span>
                <span style={{
                  fontSize: '0.74rem',
                  fontWeight: 700,
                  padding: '2px 8px',
                  borderRadius: '12px',
                  backgroundColor: doc?.statusCode === 'EXECUTED' ? '#dcfce7' : '#eff6ff',
                  color: doc?.statusCode === 'EXECUTED' ? '#166534' : '#1d4ed8',
                  border: `1px solid ${doc?.statusCode === 'EXECUTED' ? '#bbf7d0' : '#bfdbfe'}`
                }}>
                  {doc?.statusCode}
                </span>
              </div>
              <div style={{ fontSize: '12px', color: 'var(--cp-muted)' }}>
                {doc?.counterpartyCompany?.name} · Ref: <strong>{doc?.documentNumber}</strong>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Zoom Controls */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              backgroundColor: '#ffffff',
              padding: '2px 6px',
              borderRadius: '5px',
              border: '1px solid #cbd5e1'
            }}>
              <button
                type="button"
                onClick={() => setZoomLevel(z => Math.max(70, z - 10))}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', padding: '0 4px', color: '#475569' }}
                title="Zoom Out"
              >
                -
              </button>
              <span style={{ fontSize: '0.74rem', fontWeight: 650, color: '#334155', minWidth: '34px', textAlign: 'center' }}>
                {zoomLevel}%
              </span>
              <button
                type="button"
                onClick={() => setZoomLevel(z => Math.min(140, z + 10))}
                style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontWeight: 700, fontSize: '0.85rem', padding: '0 4px', color: '#475569' }}
                title="Zoom In"
              >
                +
              </button>
            </div>

            {/* Print / Save PDF Button */}
            <button
              type="button"
              onClick={() => window.print()}
              style={{
                background: '#ffffff',
                border: '1px solid #cbd5e1',
                borderRadius: '5px',
                padding: '5px 12px',
                fontSize: '0.78rem',
                fontWeight: 600,
                color: '#475569',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}
            >
              <LuPrinter size={14} /> Print / Save
            </button>

            <div className="cp-tabs">
              <button
                type="button"
                className={`cp-tab-btn ${activeTab === 'VIEWER' ? 'active' : ''}`}
                onClick={() => setActiveTab('VIEWER')}
              >
                Document Review
              </button>
              <button
                type="button"
                className={`cp-tab-btn ${activeTab === 'REQUIREMENTS' ? 'active' : ''}`}
                onClick={() => setActiveTab('REQUIREMENTS')}
              >
                Obligations ({doc?.requirements?.filter(r => r.statusCode === 'PENDING').length || 0})
              </button>
            </div>

            <button
              onClick={handleClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cp-muted)', padding: '6px' }}
              title="Close"
            >
              <LuX size={20} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        {loading ? (
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--cp-muted)' }}>
            Loading document details...
          </div>
        ) : error ? (
          <div style={{ flex: 1, padding: '60px 40px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <div style={{ color: '#ef4444', fontSize: '15px', fontWeight: 600, marginBottom: '14px' }}>
              {error}
            </div>
            <button
              onClick={() => {
                localStorage.removeItem('portal_token');
                localStorage.removeItem('portal_user');
                navigate('/portal/login');
              }}
              className="cp-btn-primary"
              style={{ width: 'auto', padding: '8px 24px', fontSize: '13px' }}
            >
              Log In Again
            </button>
          </div>
        ) : activeTab === 'VIEWER' ? (
          <div className="cp-modal-body" style={{ gridTemplateColumns: '1fr 340px' }}>
            {/* Left: Authentic Word DOCX Multi-Page Document Layout */}
            <div style={{
              flex: 1,
              overflowY: 'auto',
              padding: '28px 20px',
              backgroundColor: '#f1f5f9',
              boxSizing: 'border-box'
            }}>
              <style>{`
                .template-document-paper {
                  background-color: #ffffff !important;
                  box-sizing: border-box !important;
                  font-family: 'Cambria', Georgia, 'Times New Roman', serif !important;
                  color: #111827 !important;
                }
                .template-document-paper * {
                  box-sizing: border-box;
                }
                .template-document-paper .doc-top-bar {
                  height: 4px !important;
                  background-color: #8E425F !important;
                  width: 100% !important;
                  margin-bottom: 12px !important;
                  border-radius: 2px !important;
                  display: block !important;
                }
                .template-document-paper .doc-title-heading {
                  font-size: 20pt !important;
                  font-weight: 700 !important;
                  color: #0f172a !important;
                  text-align: center !important;
                  margin: 18px 0 16px 0 !important;
                  letter-spacing: 0.8px !important;
                  text-transform: uppercase !important;
                  font-family: 'Cambria', Georgia, serif !important;
                }
                .template-document-paper .doc-section-heading {
                  font-size: 14pt !important;
                  font-weight: 700 !important;
                  color: #0f172a !important;
                  text-align: center !important;
                  margin: 18px 0 12px 0 !important;
                  letter-spacing: 0.6px !important;
                  text-transform: uppercase !important;
                  font-family: 'Cambria', Georgia, serif !important;
                }
                .template-document-paper p {
                  margin-bottom: 12px !important;
                  color: #111827 !important;
                  line-height: 1.65 !important;
                  text-align: justify !important;
                  text-justify: inter-word !important;
                  font-size: 11pt !important;
                  font-family: 'Cambria', Georgia, serif !important;
                }
                .template-document-paper ol {
                  padding-left: 28px !important;
                  margin: 10px 0 16px 0 !important;
                  color: #111827 !important;
                  font-family: 'Cambria', Georgia, serif !important;
                }
                .template-document-paper ul {
                  padding-left: 28px !important;
                  margin: 8px 0 14px 0 !important;
                  color: #111827 !important;
                  font-family: 'Cambria', Georgia, serif !important;
                }
                .template-document-paper li {
                  margin-bottom: 11px !important;
                  color: #111827 !important;
                  line-height: 1.65 !important;
                  text-align: justify !important;
                  text-justify: inter-word !important;
                  font-size: 11pt !important;
                  font-family: 'Cambria', Georgia, serif !important;
                }
                .template-document-paper li strong,
                .template-document-paper li u,
                .template-document-paper strong {
                  color: #0f172a !important;
                }
                .template-document-paper table {
                  width: 100% !important;
                  border-collapse: collapse !important;
                  margin: 20px 0 !important;
                  font-family: 'Cambria', Georgia, serif !important;
                }
                .template-document-paper table td,
                .template-document-paper table th {
                  border: 1px solid #94a3b8 !important;
                  padding: 10px 14px !important;
                  vertical-align: top !important;
                  font-size: 10.5pt !important;
                  color: #0f172a !important;
                  line-height: 1.5 !important;
                }
                .template-document-paper table tr td:first-child {
                  font-weight: 600 !important;
                  background-color: #f8fafc !important;
                  width: 32% !important;
                }
                .template-document-paper .doc-footer-banner {
                  margin-top: 50px !important;
                  width: 100% !important;
                  border-radius: 2px !important;
                  overflow: hidden !important;
                  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1) !important;
                  display: block !important;
                }
                .template-document-paper .doc-footer-accent {
                  height: 4px !important;
                  background-color: #8E425F !important;
                  width: 100% !important;
                  display: block !important;
                }
                .template-document-paper .doc-footer-body {
                  background-color: #01497C !important;
                  padding: 14px 24px !important;
                  display: flex !important;
                  align-items: center !important;
                  justify-content: space-between !important;
                  gap: 16px !important;
                  color: #ffffff !important;
                }
                .template-document-paper .doc-footer-text {
                  text-align: right !important;
                  font-size: 11px !important;
                  line-height: 1.45 !important;
                  color: #ffffff !important;
                  font-family: 'Cambria', Georgia, serif !important;
                }
                .template-document-paper .doc-footer-text a {
                  color: #ffffff !important;
                  text-decoration: underline !important;
                }
                @media print {
                  body * {
                    visibility: hidden;
                  }
                  .template-document-paper, .template-document-paper * {
                    visibility: visible;
                  }
                  .template-document-paper {
                    position: relative !important;
                    left: 0;
                    top: 0;
                    width: 100% !important;
                    max-width: 100% !important;
                    box-shadow: none !important;
                    border: none !important;
                    padding: 20px 40px !important;
                    page-break-after: always !important;
                    break-after: page !important;
                    page-break-inside: avoid !important;
                  }
                }
              `}</style>

              {previewLoading && (
                <div style={{ padding: '30px', color: '#64748b', textAlign: 'center' }}>
                  <LuLayers size={28} style={{ animation: 'spin 2s linear infinite', margin: '0 auto 8px auto' }} />
                  <p style={{ margin: 0, fontSize: '0.85rem' }}>Loading formal document sheets...</p>
                </div>
              )}

              {/* Render All Document Pages */}
              {rawPages.map((pageHtml, pageIdx) => {
                const processedHtml = interpolateDocData(formatDocumentHtml(pageHtml), terms, doc, pageIdx, rawPages.length);

                return (
                  <div key={pageIdx} style={{ width: '100%', marginBottom: pageIdx === rawPages.length - 1 ? '40px' : '0' }}>
                    <div
                      className="template-document-paper"
                      style={{
                        backgroundColor: '#ffffff',
                        width: '100%',
                        maxWidth: `${Math.round(860 * (zoomLevel / 100))}px`,
                        margin: '0 auto',
                        padding: `${Math.round(48 * (zoomLevel / 100))}px ${Math.round(56 * (zoomLevel / 100))}px`,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.05)',
                        border: '1px solid #e2e8f0',
                        borderRadius: '6px',
                        color: '#111827',
                        fontSize: `${14.5 * (zoomLevel / 100)}px`,
                        lineHeight: 1.65,
                        fontFamily: "'Cambria', Georgia, 'Times New Roman', serif",
                        minHeight: '750px',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        boxSizing: 'border-box'
                      }}
                    >
                      <div>
                        {/* Word Template Header Accent Bar */}
                        <div className="doc-top-bar" />

                        {/* Running Header Bar with Page Number indicator */}
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          fontSize: '0.78rem',
                          color: '#64748b',
                          marginBottom: '18px',
                          fontFamily: "'Cambria', Georgia, serif"
                        }}>
                          <span style={{ fontWeight: 600, letterSpacing: '0.2px' }}>
                            {doc.documentTypeCode === 'PO' ? 'VENDOR WORK ORDER' : 'MASTER SERVICES AGREEMENT'}
                          </span>
                          <span style={{
                            fontWeight: 700,
                            backgroundColor: '#f1f5f9',
                            color: '#475569',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            border: '1px solid #e2e8f0',
                            fontSize: '0.74rem'
                          }}>
                            Page {pageIdx + 1} of {rawPages.length}
                          </span>
                        </div>

                        {/* Document Content for this page */}
                        <div dangerouslySetInnerHTML={{ __html: processedHtml }} />
                      </div>

                      {/* Word Template Footer Banner for each page */}
                      <div className="doc-footer-banner">
                        <div className="doc-footer-accent" />
                        <div className="doc-footer-body">
                          <div className="doc-footer-logo">
                            <img
                              src="/prophecy_logo.png"
                              alt="Prophecy Technologies"
                              style={{ height: `${Math.round(34 * (zoomLevel / 100))}px`, objectFit: 'contain', display: 'block' }}
                            />
                          </div>
                          <div className="doc-footer-text">
                            <div style={{ fontWeight: 700, letterSpacing: '0.2px' }}>
                              Prophecy Consulting Inc <span style={{ opacity: 0.7, fontWeight: 400 }}>|</span> <a href="https://www.prophecytechs.com" target="_blank" rel="noopener noreferrer">www.prophecytechs.com</a>
                            </div>
                            <div>7545 Irvine Center Dr, Ste 200, Irvine, CA 92618</div>
                            <div>
                              <a href="mailto:onboarding@prophecytechs.com">onboarding@prophecytechs.com</a> <span style={{ opacity: 0.7 }}>|</span> +1(551) 689-4006
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Visual Page Break Separator Between Pages */}
                    {pageIdx < rawPages.length - 1 && (
                      <div style={{
                        maxWidth: `${Math.round(860 * (zoomLevel / 100))}px`,
                        margin: '24px auto',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px'
                      }}>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#cbd5e1' }} />
                        <span style={{
                          fontSize: '0.72rem',
                          color: '#64748b',
                          fontWeight: 600,
                          backgroundColor: '#e2e8f0',
                          padding: '3px 10px',
                          borderRadius: '12px'
                        }}>
                          Page Break &bull; End of Page {pageIdx + 1}
                        </span>
                        <div style={{ flex: 1, height: '1px', backgroundColor: '#cbd5e1' }} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Right: Signers & Timeline Overview */}
            <div className="cp-sidebar-panel">
              <div>
                <h3 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--cp-muted)', margin: '0 0 10px' }}>
                  Signers &amp; Execution Status
                </h3>
                {doc?.signers?.map(s => (
                  <div key={s.signerId} className="cp-signer-row" style={{ padding: '12px', marginBottom: '8px', background: '#f8fafc', borderRadius: '6px', border: '1px solid #e2e8f0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                      <span style={{ fontWeight: 650, fontSize: '13.5px', color: 'var(--cp-ink)' }}>
                        {s.name} {s.isMe && <span style={{ color: 'var(--cp-auth)', fontSize: '11px' }}>(You)</span>}
                      </span>
                      <span className={`cp-badge ${s.statusCode === 'SIGNED' ? 'cp-badge-executed' : s.statusCode === 'DECLINED' ? 'cp-badge-declined' : 'cp-badge-out'}`}>
                        {s.statusCode}
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--cp-muted)' }}>
                      Side: {s.partySide} &bull; Title: {s.title || 'Authorized Representative'}
                    </div>
                    {s.signedAtUtc && (
                      <div style={{ fontSize: '11.5px', color: 'var(--cp-ok)', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}>
                        <LuCheck size={12} /> Signed {new Date(s.signedAtUtc).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Status Action Box */}
              {doc?.myAction?.canSign ? (
                <div style={{ marginTop: '16px', padding: '16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#166534', fontWeight: 700, fontSize: '0.9rem', marginBottom: '6px' }}>
                    <LuPenTool size={16} /> Action Required
                  </div>
                  <p style={{ fontSize: '0.82rem', color: '#334155', margin: '0 0 12px', lineHeight: 1.45 }}>
                    Please review the legal agreement terms and clauses on the left. Click below to execute your electronic signature.
                  </p>
                  <button
                    type="button"
                    onClick={openSignModal}
                    className="cp-btn-primary"
                    style={{ width: '100%', padding: '10px 14px', fontSize: '0.88rem', fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}
                  >
                    <LuCheck size={16} /> Review &amp; Sign Agreement
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowDeclineModal(true)}
                    className="cp-btn-logout"
                    style={{ width: '100%', marginTop: '8px', padding: '8px 12px', fontSize: '0.8rem', color: 'var(--cp-danger)' }}
                  >
                    Decline Agreement
                  </button>
                </div>
              ) : (doc?.statusCode === 'EXECUTED' || (terms.govSigner?.StatusCode === 'SIGNED' && terms.cpSigner?.StatusCode === 'SIGNED')) ? (
                <div style={{ marginTop: '16px', padding: '16px', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', color: '#166534', fontWeight: 700, fontSize: '0.92rem', marginBottom: '6px' }}>
                    <LuCheck size={18} /> Fully Executed &amp; Active
                  </div>
                  <p style={{ fontSize: '0.8rem', color: '#166534', margin: 0, lineHeight: 1.45 }}>
                    All required parties have executed their signatures. This agreement is legally binding and active.
                  </p>
                </div>
              ) : null}

              {/* Document Metadata Details */}
              <div style={{ borderTop: '1px solid var(--cp-border)', marginTop: '20px', paddingTop: '16px', fontSize: '13px' }}>
                <h3 style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--cp-muted)', margin: '0 0 10px' }}>
                  Document Details
                </h3>
                <div style={{ display: 'grid', gap: '8px', color: '#475569' }}>
                  <div>Type: <strong>{doc.documentTypeCode}</strong></div>
                  <div>Effective Date: <strong>{doc.effectiveFromDate || 'Upon execution'}</strong></div>
                  <div>Document ID: <strong>#{doc.commercialDocumentId}</strong></div>
                  <div>Status: <strong>{doc.statusCode}</strong></div>
                </div>
              </div>

              <div style={{ marginTop: '24px' }}>
                <button
                  type="button"
                  className="cp-btn-logout"
                  style={{ width: '100%' }}
                  onClick={handleClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* Obligations Checklist Tab */
          <div style={{ flex: 1, padding: '32px', overflowY: 'auto' }}>
            <div style={{ maxWidth: '800px', margin: '0 auto' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 6px' }}>Post-Execution Obligations</h2>
              <p style={{ color: 'var(--cp-muted)', fontSize: '14px', margin: '0 0 24px' }}>
                Upload required compliance certificates and documents to satisfy obligations for this commercial agreement.
              </p>

              {doc?.requirements?.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: 'var(--cp-muted)', background: '#f8fafc', borderRadius: '8px' }}>
                  No post-execution requirements specified for this document.
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '12px' }}>
                  {doc?.requirements?.map(r => (
                    <div
                      key={r.requirementId}
                      style={{
                        background: 'white',
                        border: '1px solid var(--cp-border)',
                        borderRadius: '8px',
                        padding: '18px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '16px'
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: 600, fontSize: '14.5px', color: 'var(--cp-ink)' }}>
                            {r.itemName}
                          </span>
                          {r.statusCode === 'SATISFIED' ? (
                            <span className="cp-badge cp-badge-executed">
                              <LuCircleCheck size={12} /> SATISFIED
                            </span>
                          ) : (
                            <span className="cp-badge cp-badge-out">
                              <LuClock size={12} /> OPEN
                            </span>
                          )}
                        </div>
                        {r.fileName && (
                          <div style={{ fontSize: '12px', color: 'var(--cp-muted)', marginTop: '4px' }}>
                            File: {r.fileName} &bull; Satisfied: {new Date(r.satisfiedAtUtc).toLocaleDateString()}
                          </div>
                        )}
                      </div>

                      <div>
                        {r.statusCode === 'SATISFIED' ? (
                          <span style={{ color: 'var(--cp-ok)', fontSize: '13px', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <LuCheck size={16} /> Completed
                          </span>
                        ) : uploadingReqId === r.requirementId ? (
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <input
                              type="file"
                              onChange={(e) => setUploadFile(e.target.files[0])}
                              style={{ fontSize: '12px' }}
                            />
                            <button
                              type="button"
                              onClick={() => handleUploadRequirement(r.requirementId)}
                              className="cp-btn-primary"
                              style={{ padding: '6px 12px', fontSize: '12px', width: 'auto' }}
                              disabled={uploading || !uploadFile}
                            >
                              {uploading ? 'Uploading...' : 'Submit'}
                            </button>
                            <button
                              type="button"
                              onClick={() => { setUploadingReqId(null); setUploadFile(null); }}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--cp-muted)' }}
                            >
                              <LuX size={16} />
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setUploadingReqId(r.requirementId)}
                            className="cp-btn-primary"
                            style={{ padding: '6px 14px', fontSize: '12.5px', width: 'auto' }}
                          >
                            <LuUpload size={14} /> Upload Document
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── Sign Modal (Type / Draw / Upload) ────────────────────────── */}
        {showSignModal && (
          <div className="cp-modal-overlay" style={{ zIndex: 120 }}>
            <div className="cp-sig-modal-card">
              {/* Modal Header */}
              <div className="cp-sig-modal-header">
                <div className="cp-sig-modal-title-row">
                  <h2 className="cp-sig-modal-title">Electronic Signature</h2>
                  <button
                    type="button"
                    className="cp-sig-modal-close-btn"
                    onClick={() => setShowSignModal(false)}
                    aria-label="Close"
                  >
                    <LuX size={20} />
                  </button>
                </div>
                <p className="cp-sig-modal-subtitle">
                  By signing below, you confirm you are authorized to bind{' '}
                  <strong>{doc?.counterpartyCompany?.name || terms?.providerLegalName || 'Company'}</strong>{' '}
                  to the terms of {doc?.documentNumber || 'this agreement'}.
                </p>
              </div>

              <form onSubmit={handleSign} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, margin: 0 }}>
                {/* Scrollable Body */}
                <div className="cp-sig-modal-body">
                  {/* Sign Mode Tabs */}
                  <div className="cp-sig-tabs">
                    <button
                      type="button"
                      className={`cp-sig-tab-btn ${signMode === 'TYPE' ? 'active' : ''}`}
                      onClick={() => setSignMode('TYPE')}
                    >
                      <LuType size={16} /> Type Name
                    </button>
                    <button
                      type="button"
                      className={`cp-sig-tab-btn ${signMode === 'DRAW' ? 'active' : ''}`}
                      onClick={() => setSignMode('DRAW')}
                    >
                      <LuPenTool size={16} /> Draw Direct
                    </button>
                    <button
                      type="button"
                      className={`cp-sig-tab-btn ${signMode === 'UPLOAD' ? 'active' : ''}`}
                      onClick={() => setSignMode('UPLOAD')}
                    >
                      <LuImage size={16} /> Upload Sign
                    </button>
                  </div>

                  {/* Signatory Name Field */}
                  <div className="cp-form-group" style={{ marginBottom: '10px' }}>
                    <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>
                      Signatory Full Legal Name *
                    </label>
                    <input
                      type="text"
                      className="cp-input"
                      value={typedName}
                      onChange={(e) => handleTypedNameChange(e.target.value)}
                      placeholder="e.g. Johnathan Doe"
                      required
                    />
                  </div>

                  {/* MODE A: Type */}
                  {signMode === 'TYPE' && (
                    <>
                      <div>
                        <label className="cp-section-label">CHOOSE SIGNATURE STYLE:</label>
                        <div className="cp-font-grid">
                          {SIGNATURE_FONTS.map(f => {
                            const isSelected = selectedFont === f.id;
                            return (
                              <div
                                key={f.id}
                                className={`cp-font-card ${isSelected ? 'selected' : ''}`}
                                onClick={() => handleSelectFont(f.id)}
                              >
                                <div className="cp-font-card-header">
                                  <span className="cp-font-card-label">{f.label || f.id.toUpperCase()}</span>
                                  {isSelected && (
                                    <span className="cp-font-card-check">
                                      <LuCircleCheck size={14} />
                                    </span>
                                  )}
                                </div>
                                <div
                                  className="cp-font-card-preview"
                                  style={{ fontFamily: `'${f.id}', cursive` }}
                                >
                                  {typedName || 'Your Signature'}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      <div className="cp-sig-preview-bar">
                        <span className="cp-sig-preview-title">SIGNATURE PREVIEW (HANDWRITING FONT)</span>
                        <div className="cp-sig-preview-content">
                          <span style={{ fontFamily: `'${selectedFont}', cursive`, fontSize: '24px', color: '#0f172a' }}>
                            {typedName || 'Your Signature'}
                          </span>
                        </div>
                      </div>
                    </>
                  )}

                  {/* MODE B: Draw */}
                  {signMode === 'DRAW' && (
                    <div className="cp-form-group">
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                        <label style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#334155' }}>Draw Signature on Pad Below *</label>
                        <button
                          type="button"
                          onClick={clearCanvas}
                          className="cp-canvas-btn"
                          style={{ fontSize: '11px', padding: '3px 8px' }}
                        >
                          <LuRotateCcw size={13} /> Clear
                        </button>
                      </div>
                      <div className="cp-canvas-wrapper">
                        <canvas
                          ref={canvasRef}
                          width={516}
                          height={160}
                          className="cp-canvas-element"
                          onMouseDown={startDrawing}
                          onMouseMove={draw}
                          onMouseUp={stopDrawing}
                          onMouseLeave={stopDrawing}
                          onTouchStart={startDrawing}
                          onTouchMove={draw}
                          onTouchEnd={stopDrawing}
                        />
                        {!hasDrawn && (
                          <div className="cp-canvas-guide">
                            <span>Sign on the line above</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* MODE C: Upload */}
                  {signMode === 'UPLOAD' && (
                    <div className="cp-form-group">
                      <label style={{ fontSize: '12px', fontWeight: 600, color: '#334155' }}>Upload Signature Image (PNG, JPG, SVG) *</label>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        style={{ display: 'none' }}
                        onChange={handleSignatureFileUpload}
                      />
                      {!uploadedSigData ? (
                        <div
                          className="cp-upload-dropzone"
                          onClick={() => fileInputRef.current?.click()}
                        >
                          <LuUpload size={28} style={{ color: 'var(--cp-muted)' }} />
                          <span style={{ fontSize: '13.5px', fontWeight: 600 }}>Click to browse signature image</span>
                          <span style={{ fontSize: '11.5px', color: 'var(--cp-muted)' }}>Max file size: 2MB (transparent PNG recommended)</span>
                        </div>
                      ) : (
                        <div className="cp-upload-preview-card">
                          <img src={uploadedSigData} alt="Signature Upload" className="cp-upload-preview-img" />
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: 'var(--cp-muted)' }}>{uploadedFileName}</span>
                            <button
                              type="button"
                              onClick={clearUploadedSignature}
                              style={{ background: 'none', border: 'none', color: 'var(--cp-danger)', cursor: 'pointer' }}
                              title="Remove Image"
                            >
                              <LuTrash2 size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Consent Checkbox */}
                  <div className="cp-consent-row">
                    <input
                      type="checkbox"
                      id="legal-consent"
                      className="cp-consent-checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                      required
                    />
                    <label htmlFor="legal-consent" className="cp-consent-label">
                      I agree that my typed name constitutes my legal electronic signature under applicable law (UETA / ESIGN / IT Act), and I intend to execute this agreement.
                    </label>
                  </div>

                  {signError && (
                    <div style={{ color: 'var(--cp-danger)', fontSize: '13px', marginTop: '10px' }}>
                      {signError}
                    </div>
                  )}
                </div>

                {/* Fixed / Pinned Action Footer */}
                <div className="cp-sig-modal-footer">
                  <button
                    type="button"
                    className="cp-btn-logout"
                    onClick={() => setShowSignModal(false)}
                    disabled={signing}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="cp-btn-primary"
                    disabled={
                      signing ||
                      !consent ||
                      !typedName.trim() ||
                      (signMode === 'DRAW' && (!hasDrawn || !drawnSigData)) ||
                      (signMode === 'UPLOAD' && !uploadedSigData)
                    }
                  >
                    {signing ? 'Signing...' : 'Confirm & Sign'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* ─── Decline Modal ────────────────────────────────────────────────── */}
        {showDeclineModal && (
          <div className="cp-modal-overlay" style={{ zIndex: 120 }}>
            <div className="cp-login-card" style={{ maxWidth: '480px', padding: '32px' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 700, margin: '0 0 8px', color: 'var(--cp-danger)' }}>
                Decline to Sign Document
              </h2>
              <p style={{ color: 'var(--cp-muted)', fontSize: '13.5px', margin: '0 0 16px' }}>
                Please state why you are declining to sign {doc?.documentNumber}. This will be communicated to the Prophecy team.
              </p>

              <form onSubmit={handleDecline}>
                <div className="cp-form-group">
                  <label>Decline Reason *</label>
                  <textarea
                    className="cp-input"
                    rows={4}
                    value={declineReason}
                    onChange={(e) => setDeclineReason(e.target.value)}
                    placeholder="e.g. Terms require revision by our legal counsel regarding clause 4..."
                    required
                    autoFocus
                  />
                </div>

                <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                  <button
                    type="button"
                    className="cp-btn-logout"
                    style={{ width: '50%' }}
                    onClick={() => setShowDeclineModal(false)}
                    disabled={declining}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="cp-btn-primary"
                    style={{ width: '50%', background: 'var(--cp-danger)' }}
                    disabled={declining || !declineReason.trim()}
                  >
                    {declining ? 'Submitting...' : 'Confirm Decline'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalDocumentViewer;
